import { CaregiverFilterSelect } from '@app/components/query-filters/controls/CaregiverFilterSelect';
import { ClientCategoryFilterSelect } from '@app/components/query-filters/controls/ClientCategoryFilterSelect';
import { ClientFilterSelect } from '@app/components/query-filters/controls/ClientFilterSelect';
import { DoctorFilterSelect } from '@app/components/query-filters/controls/DoctorFilterSelect';
import { ServiceFilterSelect } from '@app/components/query-filters/controls/ServiceFilterSelect';
import { FilterColumn } from '@app/components/query-filters/filters.types';
import type { TFunction } from 'i18next';

// The client-facing money a doctor is not served. Filtering on a field the
// backend strips is a way back to the number the columns refuse to show, which is
// why the filter list drops them too rather than leaving them to return nothing.
const CLIENT_PRICING_FILTERS = new Set(['price_total', 'discount_percent', 'price_final']);

export const getVisitFilterColumns = (
  t: TFunction,
  showInternalFinancials: boolean = true,
  showClientPricing: boolean = true
): FilterColumn[] => {
  const columns: FilterColumn[] = [
    {
      name: 'client_id',
      label: t('visits-filter-patient'),
      type: 'custom',
      component: ClientFilterSelect,
      operators: ['==', 'contains_in', 'overlap', 'contained_in'],
    },
    {
      name: 'caregiver_id',
      label: t('visits-filter-caregiver'),
      type: 'custom',
      component: CaregiverFilterSelect,
      operators: ['==', 'contains_in', 'overlap', 'contained_in'],
    },
    {
      name: 'service_ids',
      label: t('visits-filter-services'),
      type: 'custom',
      component: ServiceFilterSelect,
      operators: ['==', 'contains_in', 'overlap', 'contained_in'],
    },
    // Date and time filters
    {
      name: 'start_time',
      label: t('visits-filter-start-time'),
      type: 'date',
    },
    {
      name: 'end_time',
      label: t('visits-filter-end-time'),
      type: 'date',
    },
    {
      name: 'completed_at',
      label: t('visits-filter-completed-at'),
      type: 'date',
    },
    {
      name: 'cancel_date',
      label: t('visits-filter-cancel-date'),
      type: 'date',
    },
    // Visit fields
    {
      name: 'status',
      label: t('visits-filter-status'),
      type: 'dropdown',
      options: [
        { label: t('visits-status-scheduled'), value: 'SCHEDULED' },
        { label: t('visits-status-in-progress'), value: 'IN_PROGRESS' },
        { label: t('visits-status-completed'), value: 'COMPLETED' },
        { label: t('visits-status-cancelled'), value: 'CANCELLED' },
      ],
    },
    {
      name: 'street',
      label: t('visits-filter-street'),
      type: 'text',
    },
    {
      name: 'city',
      label: t('visits-filter-city'),
      type: 'text',
    },
    {
      name: 'postal_code',
      label: t('visits-filter-postal-code'),
      type: 'text',
    },
    {
      name: 'cancel_reason',
      label: t('visits-filter-cancel-reason'),
      type: 'text',
    },
    // Financial fields
    {
      name: 'cost_total',
      label: t('visits-filter-cost-total'),
      type: 'number',
    },
    {
      name: 'price_total',
      label: t('visits-filter-price-total'),
      type: 'number',
    },
    {
      name: 'discount_percent',
      label: t('visits-filter-discount-percent'),
      type: 'number',
    },
    {
      name: 'price_final',
      label: t('visits-filter-final-price'),
      type: 'number',
    },
    // Boolean and array filters
    {
      name: 'has_attachments',
      label: t('visits-filter-has-attachments'),
      type: 'boolean',
      options: [
        { label: t('common-yes'), value: 1 },
        { label: t('common-no'), value: 0 },
      ],
    },

    {
      name: 'has_assigned_caregivers',
      label: t('visits-filter-has-assigned-caregivers'),
      type: 'boolean',
      options: [
        { label: t('common-yes'), value: 1 },
        { label: t('common-no'), value: 0 },
      ],
    },

    // Client-related filters

    {
      name: 'client_first_name',
      label: t('visits-filter-patient-first-name'),
      type: 'text',
    },
    {
      name: 'client_last_name',
      label: t('visits-filter-patient-last-name'),
      type: 'text',
    },
    {
      name: 'client_email',
      label: t('visits-filter-patient-email'),
      type: 'text',
    },
    {
      name: 'client_phone',
      label: t('visits-filter-patient-phone'),
      type: 'text',
    },
    {
      name: 'client_city',
      label: t('visits-filter-patient-city'),
      type: 'text',
    },
    {
      name: 'client_category_ids',
      label: t('visits-filter-patient-categories'),
      type: 'custom',
      component: ClientCategoryFilterSelect,
      operators: ['overlap', 'contains_in', 'contained_in'],
    },

    // Caregiver-related filters

    {
      name: 'caregiver_first_name',
      label: t('visits-filter-caregiver-first-name'),
      type: 'text',
    },
    {
      name: 'caregiver_last_name',
      label: t('visits-filter-caregiver-last-name'),
      type: 'text',
    },
    {
      name: 'caregiver_email',
      label: t('visits-filter-caregiver-email'),
      type: 'text',
    },
    {
      name: 'caregiver_phone',
      label: t('visits-filter-caregiver-phone'),
      type: 'text',
    },
    {
      name: 'caregiver_city',
      label: t('visits-filter-caregiver-city'),
      type: 'text',
    },

    // Doctor-related filters

    {
      name: 'attending_doctor_id',
      label: t('visits-filter-attending-doctor'),
      type: 'custom',
      component: DoctorFilterSelect,
      operators: ['=='],
    },
    {
      name: 'referring_doctor_id',
      label: t('visits-filter-referring-doctor'),
      type: 'custom',
      component: DoctorFilterSelect,
      operators: ['=='],
    },
  ];

  return columns.filter(
    (column) =>
      (showInternalFinancials || column.name !== 'cost_total') &&
      (showClientPricing || !CLIENT_PRICING_FILTERS.has(column.name))
  );
};
